import { Button, Modal, Select } from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { QuizUserCollResTypes } from "src/static/interface";
import {
  GenerateQuizConfig,
  GetUserQuizCollectionsConfig,
} from "src/service/api";
import { Link } from "react-router-dom";
import {
  PlayCircleOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import ShareOnButton from "src/pages/admin/test/ShareOnButton";
import { useTranslation } from "src/i18n/context";
import dayjs from "dayjs";
import { catchFunc } from "src/utils";
import { useUserStore } from "src/store/userStore";

const { Option } = Select;

function QuizesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const [dataByStatus, setDataByStatus] = useState<
    Record<"Boshlanmagan" | "Jarayonda" | "Yakunlangan", QuizUserCollResTypes[]>
  >({
    Boshlanmagan: [],
    Jarayonda: [],
    Yakunlangan: [],
  });
  const [loading, setLoading] = useState(false);
  const [languageModalVisible, setLanguageModalVisible] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState<number | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("UZ");
  
  const universities = useMemo(() => {
    if (!userInfo) return [];
    
    if (userInfo.universitiesInfo && userInfo.universitiesInfo.length > 0) {
      return userInfo.universitiesInfo.map((uni) => ({
        code: uni.universityCode,
        name: uni.universityName,
      }));
    }
    

    return [];
  }, [userInfo]);
  
  const SELECTED_UNIVERSITY_KEY = "SURVEY_SELECTED_UNIVERSITY_CODE";
  
  const [selectedUniversityCode, setSelectedUniversityCode] = useState<string | null>(null);
  
  const handleUniversityChange = (value: string) => {
    setSelectedUniversityCode(value);
    localStorage.setItem(SELECTED_UNIVERSITY_KEY, value);
  };
  
  useEffect(() => {
    if (universities.length === 0) return;
    
    const savedUniversityCode = localStorage.getItem(SELECTED_UNIVERSITY_KEY);
    
    if (savedUniversityCode) {
      const isValid = universities.some(uni => uni.code === savedUniversityCode);
      if (isValid) {
        setSelectedUniversityCode(savedUniversityCode);
        return;
      } else {
        localStorage.removeItem(SELECTED_UNIVERSITY_KEY);
      }
    }
    
    if (universities.length === 1) {
      setSelectedUniversityCode(universities[0].code);
      localStorage.setItem(SELECTED_UNIVERSITY_KEY, universities[0].code);
    } else if (universities.length > 1 && !selectedUniversityCode) {
      setSelectedUniversityCode(universities[0].code);
      localStorage.setItem(SELECTED_UNIVERSITY_KEY, universities[0].code);
    }
  }, [universities]);

  const STATUSES: Array<"Boshlanmagan" | "Jarayonda" | "Yakunlangan"> = useMemo(
    () => ["Boshlanmagan", "Jarayonda", "Yakunlangan"],
    []
  );

  const getFunc = () => {
    if (!selectedUniversityCode) {
      return;
    }
    
    setLoading(true);
    Promise.all(
      STATUSES.map((s) => {
        const params = `?status=${encodeURIComponent(s)}&universityCode=${encodeURIComponent(selectedUniversityCode)}`;
        return GetUserQuizCollectionsConfig(params)
          .then((res) => ({ status: s, content: res.data?.content || [] }))
          .catch((err) => {
            catchFunc(err);
            return { status: s, content: [] as QuizUserCollResTypes[] };
          });
      })
    )
      .then((results) => {
        const next: Record<
          "Boshlanmagan" | "Jarayonda" | "Yakunlangan",
          QuizUserCollResTypes[]
        > = {
          Boshlanmagan: [],
          Jarayonda: [],
          Yakunlangan: [],
        };
        results.forEach((r) => {
          next[r.status] = r.content;
        });
        setDataByStatus(next);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getFunc();
  }, [selectedUniversityCode]);

  const startTest = (id: number) => {
    setSelectedQuizId(id);
    setSelectedLanguage("UZ");
    setLanguageModalVisible(true);
  };

  const handleStartQuiz = () => {
    if (selectedQuizId && selectedUniversityCode) {
      GenerateQuizConfig(selectedQuizId, selectedLanguage, selectedUniversityCode).then(() => {
        setLanguageModalVisible(false);
        navigate(`/quizes/solve?id=${selectedQuizId}${selectedUniversityCode ? `&universityCode=${encodeURIComponent(selectedUniversityCode)}` : ""}`);
      });
    }
  };

  const renderCards = (list: QuizUserCollResTypes[]) => {
    return (
      <>
        {list.length > 0 ? (
          list.map((item) => (
            <div key={crypto?.randomUUID()} className="testinfo__card">
              <div className="testinfo__card__left">
                <h2>{item.quizRuleProjection.theme}</h2>
                <p>
                  {t("quizzes.endTime")}:{" "}
                  <span>
                    {dayjs(item.quizRuleProjection.endTime).format(
                      "YYYY-MM-DD H:mm"
                    )}
                  </span>
                </p>
              </div>

              <div className="testinfo__card__right">
                {item.status === "Boshlanmagan" && (
                  <Button
                    type="primary"
                    onClick={() => startTest(item.quizRuleProjection.id)}
                    icon={<PlayCircleOutlined />}
                  >
                    {t("quizzes.startQuiz")}
                  </Button>
                )}
                {item.status === "Jarayonda" && (
                  <Link to={`/quizes/solve?id=${item.quizRuleProjection.id}${selectedUniversityCode ? `&universityCode=${encodeURIComponent(selectedUniversityCode)}` : ""}`}>
                    <Button type="primary" icon={<RetweetOutlined />}>
                      {t("quizzes.continueQuiz")}
                    </Button>
                  </Link>
                )}
                {item.status === "Yakunlangan" && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, color: '#666', marginRight: 4 }}>Ulashish:</span>
                    <ShareOnButton.Telegram 
                      url="https://survey.hemis.uz/" 
                      text={`Men https://survey.hemis.uz/ tizimida ${item.quizRuleProjection.theme}da  qatnashdim. Siz ham o'z fikringizni bildiring!`} 
                    />
                      <ShareOnButton.Instagram 
                      url="https://survey.hemis.uz/" 
                      text={`Men https://survey.hemis.uz/ tizimida ${item.quizRuleProjection.theme}da  qatnashdim. Siz ham o'z fikringizni bildiring!`}
                    />
                    <ShareOnButton.LinkedIn 
                      url="https://survey.hemis.uz/" 
                      text={`Men https://survey.hemis.uz/ tizimida ${item.quizRuleProjection.theme}da  qatnashdim. Siz ham o'z fikringizni bildiring!`}
                    />
                    <ShareOnButton.Twitter 
                      url="https://survey.hemis.uz/" 
                      text={`Men https://survey.hemis.uz/ tizimida ${item.quizRuleProjection.theme}da  qatnashdim. Siz ham o'z fikringizni bildiring!`} 
                    />
                    <ShareOnButton.Facebook 
                      url="https://survey.hemis.uz/" 
                      text={`Men https://survey.hemis.uz/ tizimida ${item.quizRuleProjection.theme}da  qatnashdim. Siz ham o'z fikringizni bildiring!`}
                    />
                    
                  
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="quiz-empty">
            {loading ? t("common.loading") : t("common.notAvailable")}
          </div>
        )}
      </>
    );
  };

  if (!userInfo) {
    return (
      <div className="testinfo">
        <div className="quiz-empty">{t("common.loading")}</div>
      </div>
    );
  }

  if (universities.length === 0) {
    return (
      <div className="testinfo">
        <div className="quiz-empty">{t("quizzes.noUniversity")}</div>
      </div>
    );
  }

  return (
    <div className="testinfo">
      {universities.length > 1 && (
        <div style={{ marginBottom: 24, maxWidth: 400 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
            {t("quizzes.selectUniversity")}:
          </label>
          <Select
            value={selectedUniversityCode}
            style={{ width: "100%" }}
            size="large"
            onChange={handleUniversityChange}
          >
            {universities.map((uni) => (
              <Option key={uni.code} value={uni.code}>
                {uni.name}
              </Option>
            ))}
          </Select>
        </div>
      )}
      
      <div className="quiz-sections">
        <div className="quiz-section">
          <div className="quiz-section__header">
            <h3>{t("quizzes.newQuizzes")}</h3>
            <span className="quiz-section__count">
              {dataByStatus.Boshlanmagan.length}
            </span>
          </div>
          {renderCards(dataByStatus.Boshlanmagan)}
        </div>

        <div className="quiz-section">
          <div className="quiz-section__header">
            <h3>{t("quizzes.inProgress")}</h3>
            <span className="quiz-section__count">
              {dataByStatus.Jarayonda.length}
            </span>
          </div>
          {renderCards(dataByStatus.Jarayonda)}
        </div>

        <div className="quiz-section">
          <div className="quiz-section__header">
            <h3>{t("quizzes.completed")}</h3>
            <span className="quiz-section__count">
              {dataByStatus.Yakunlangan.length}
            </span>
          </div>
          {renderCards(dataByStatus.Yakunlangan)}
        </div>
      </div>
      
      <Modal
        title={t("quizzes.selectLanguageModal")}
        open={languageModalVisible}
        onOk={handleStartQuiz}
        onCancel={() => setLanguageModalVisible(false)}
        okText={t("quizzes.startQuiz")}
        cancelText={t("common.cancel")}
        width={500}
      >
        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8 }}>{t("quizzes.selectLanguage")}</p>
          <Select
            value={selectedLanguage}
            style={{ width: "100%" }}
            size="large"
            onChange={(value) => setSelectedLanguage(value)}
          >
            <Option value="UZ">O'zbekcha</Option>
            <Option value="RU">Русский</Option>
            <Option value="KK">Qaraqalpaqsha</Option>
            <Option value="EN">English</Option>
          </Select>
        </div>
      </Modal>
    </div>
  );
}

export default QuizesPage;
